import type {PlatformMediaType, SocialPost} from '@Components/social-media/post.vo';
import type {BaseMetrics, SocialPlatformMetrics} from '@Components/social-media/social-platform-metrics.types';
import {MetricsAvailability} from '@Components/social-media/social-platform-metrics.types';
import {getReadableAccounts} from '@Components/social-media/social-media.types';

export const getAccountMetricsAvailability = (post: SocialPost, accountId: number): MetricsAvailability => {
  const mediaTypeParams = post.publishingParams[String(accountId)];
  if (!mediaTypeParams) {
    return MetricsAvailability.AVAILABLE;
  }

  const params = mediaTypeParams.POST ?? mediaTypeParams.REELS ?? mediaTypeParams.STORIES;
  return params?.metricsAvailability ?? MetricsAvailability.AVAILABLE;
};

export const getAccountMediaTypeMetricsAvailability = (post: SocialPost, accountId: number, mediaType: PlatformMediaType): MetricsAvailability => {
  const mediaTypeParams = post.publishingParams[String(accountId)];
  if (!mediaTypeParams) {
    return MetricsAvailability.AVAILABLE;
  }

  return mediaTypeParams[mediaType]?.metricsAvailability ?? MetricsAvailability.AVAILABLE;
};

export const getMetricsForAccount = (post: SocialPost, accountId: number, mediaType: PlatformMediaType): SocialPlatformMetrics | undefined => {
  return post.metrics?.[String(accountId)]?.[mediaType];
};

export const hasMetricsForAccountMediaType = (post: SocialPost, accountId: number, mediaType: PlatformMediaType): boolean => {
  return Boolean(getMetricsForAccount(post, accountId, mediaType));
};

export const hasAnyMetricsForAccount = (post: SocialPost, accountId: number): boolean => {
  const accountBlob = post.metrics?.[String(accountId)];
  return !!accountBlob && Object.keys(accountBlob).length > 0;
};

export const countAccountsWithAnyMetrics = (post: SocialPost): number => {
  if (!post.metrics) {
    return 0;
  }
  return Object.keys(post.metrics).filter((accountId) => hasAnyMetricsForAccount(post, Number(accountId))).length;
};

const forEachInsight = (post: SocialPost, callback: (insights: SocialPlatformMetrics) => void): void => {
  if (!post.metrics) {
    return;
  }
  for (const accountBlob of Object.values(post.metrics)) {
    for (const insights of Object.values(accountBlob)) {
      if (insights) {
        callback(insights);
      }
    }
  }
};

export const sumBaseMetricsAcrossAllAccounts = (post: SocialPost, ...keys: (keyof BaseMetrics)[]): number => {
  let total = 0;
  forEachInsight(post, (insights) => {
    const baseMetrics = insights as BaseMetrics | undefined;
    for (const key of keys) {
      total += baseMetrics?.[key] ?? 0;
    }
  });
  return total;
};

export const sumBaseMetricForAccount = (post: SocialPost, accountId: number, key: keyof BaseMetrics): number => {
  const accountBlob = post.metrics?.[String(accountId)];
  if (!accountBlob) {
    return 0;
  }
  let total = 0;
  for (const insights of Object.values(accountBlob)) {
    total += (insights as BaseMetrics | undefined)?.[key] ?? 0;
  }
  return total;
};

export const getPlatformsForAvailability = (post: SocialPost, reason: MetricsAvailability): string => {
  const names = new Set<string>();
  for (const account of post.accounts) {
    if (getAccountMetricsAvailability(post, account.id) === reason) {
      names.add(getReadableAccounts(account.type));
    }
  }

  return Array.from(names).join(', ');
};
